import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import resultsService from '../../services/resultsService';
import scheduleService from '../../services/scheduleService';
import participantsService from '../../services/participantsService';
import { groupsService } from '../../services';
import TailwindPageWrapper from "../generic/TailwindPageWrapper";

const ScorekeeperView = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  
  // Venue des Scorekeepers - wenn keine zugewiesen, kann der User wählen
  const assignedVenueFromUser = user?.venue;
  const [selectedVenue, setSelectedVenue] = useState(assignedVenueFromUser || "");
  const assignedVenue = assignedVenueFromUser || selectedVenue;
  
  const [loading, setLoading] = useState(true);
  const [currentEvent, setCurrentEvent] = useState(null);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [upcomingEventsWithCounts, setUpcomingEventsWithCounts] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [editedData, setEditedData] = useState(new Map());
  const [savedResults, setSavedResults] = useState(new Map()); // Map von participantId -> resultId
  const [isSaving, setIsSaving] = useState(false);
  const [savingParticipants, setSavingParticipants] = useState(new Set()); // Set von participantIds die gerade gespeichert werden
  const [toast, setToast] = useState({ show: false, message: "", type: "info" });
  const [participantStatuses, setParticipantStatuses] = useState(new Map()); // Map von participantId -> { didNotStart, isLate }

  useEffect(() => {
    if (assignedVenue) {
      loadData();
    }
  }, [assignedVenue]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Lade Schedule-Events für die zugewiesene Fläche
      const scheduleResult = await scheduleService.getAllScheduleEvents({ venue: assignedVenue });
      const scheduleEvents = scheduleResult.success ? scheduleResult.data || [] : [];
      
      // Sortiere nach Startzeit
      scheduleEvents.sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
      
      // Finde aktuelles Event (Status "Aktiv")
      const activeEvent = scheduleEvents.find(e => e.status === 'Aktiv');
      setCurrentEvent(activeEvent || null);
      
      // Finde nächste 2 Events (Status "Ausstehend" oder "Vorbereitung")
      const pendingEvents = scheduleEvents.filter(e => e.status === 'Ausstehend' || e.status === 'Vorbereitung').slice(0, 2);
      setUpcomingEvents(pendingEvents);
      
      // Lade Teilnehmeranzahl für kommende Events
      if (pendingEvents.length > 0) {
        const participantsResult = await participantsService.getAllParticipants();
        if (participantsResult.success) {
          const allParticipants = participantsResult.data || [];
          const eventsWithCounts = pendingEvents.map(event => {
            if (event.group?.id) {
              const eventParticipants = allParticipants.filter(p => {
                if (!p.groups || !Array.isArray(p.groups)) return false;
                return p.groups.some(g => g.id === event.group.id);
              });
              return {
                ...event,
                participantCount: eventParticipants.length
              };
            }
            return {
              ...event,
              participantCount: 0
            };
          });
          setUpcomingEventsWithCounts(eventsWithCounts);
        } else {
          setUpcomingEventsWithCounts(pendingEvents.map(e => ({ ...e, participantCount: 0 })));
        }
      } else {
        setUpcomingEventsWithCounts([]);
      }
      
      // Lade Teilnehmer für aktuelles Event
      if (activeEvent && activeEvent.group) {
        const participantsResult = await participantsService.getAllParticipants();
        if (participantsResult.success) {
          const allParticipants = participantsResult.data || [];
          const eventParticipants = allParticipants.filter(p => {
            if (!p.groups || !Array.isArray(p.groups)) return false;
            return p.groups.some(g => g.id === activeEvent.group.id);
          });
          
          eventParticipants.sort((a, b) => {
            const aGroup = a.groups.find(g => g.id === activeEvent.group.id);
            const bGroup = b.groups.find(g => g.id === activeEvent.group.id);
            const aStartNumber = aGroup ? aGroup.startNumber : 999;
            const bStartNumber = bGroup ? bGroup.startNumber : 999;
            return aStartNumber - bStartNumber;
          });
          
          setParticipants(eventParticipants);
          
          // Lade bestehende Ergebnisse
          if (activeEvent.group.id) {
            await loadResults(activeEvent.group.id);
          }
        }
      } else {
        setParticipants([]);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Daten:', error);
      addToast('error', 'Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const addToast = (type, message) => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: "", type: "info" }), 5000);
  };

  const formatTime = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
  };

  const handleScoreChange = (participantId, judgeIndex, newScore) => {
    let score = isNaN(newScore) ? 0 : parseFloat(newScore);
    if (score > 10) {
      addToast('error', 'Wertungsrichter-Punkte dürfen maximal 10.00 betragen');
      score = 10;
    }
    if (score < 0) score = 0;
    
    setEditedData(prev => {
      const newMap = new Map(prev);
      const participantData = newMap.get(participantId) || {
        judgeScores: [0, 0, 0, 0, 0],
        penalty: 0
      };
      
      const updatedScores = [...participantData.judgeScores];
      updatedScores[judgeIndex] = score;
      
      const averageScore = updatedScores.reduce((sum, s) => sum + s, 0) / updatedScores.length;
      const finalScore = averageScore + (participantData.penalty || 0);
      
      newMap.set(participantId, {
        ...participantData,
        judgeScores: updatedScores,
        finalScore: finalScore.toFixed(2)
      });
      
      return newMap;
    });
  };

  const handlePenaltyChange = (participantId, newPenalty) => {
    const penalty = isNaN(newPenalty) ? 0 : parseFloat(newPenalty);
    
    setEditedData(prev => {
      const newMap = new Map(prev);
      const participantData = newMap.get(participantId) || {
        judgeScores: [0, 0, 0, 0, 0],
        penalty: 0
      };
      
      const averageScore = participantData.judgeScores.reduce((sum, s) => sum + s, 0) / participantData.judgeScores.length;
      const finalScore = averageScore + penalty;
      
      newMap.set(participantId, {
        ...participantData,
        penalty,
        finalScore: finalScore.toFixed(2)
      });
      
      return newMap;
    });
  };


  const handleSaveParticipantResult = async (participantId) => {
    if (!currentEvent || !currentEvent.group) return;
    
    const participantData = editedData.get(participantId);
    if (!participantData) return;
    
    // Validierung: Alle 5 Wertungsrichter-Punkte müssen ausgefüllt sein
    const hasAllJudgeScores = participantData.judgeScores.every(score => 
      score !== null && score !== undefined && score !== '' && !isNaN(parseFloat(score))
    );
    
    if (!hasAllJudgeScores) {
      addToast('error', 'Bitte alle 5 Wertungsrichter-Punkte eingeben');
      return;
    }
    
    // Prüfe ob alle Punkte > 0 sind
    const allScoresValid = participantData.judgeScores.every(score => {
      const numScore = parseFloat(score);
      return !isNaN(numScore) && numScore >= 0 && numScore <= 10;
    });
    
    if (!allScoresValid) {
      addToast('error', 'Wertungsrichter-Punkte müssen zwischen 0 und 10 liegen');
      return;
    }
    
    try {
      setSavingParticipants(prev => new Set(prev).add(participantId));
      
      const groupId = currentEvent.group.id;
      const resultId = savedResults.get(participantId);
      
      const resultData = {
        groupId,
        participantId,
        judgeScore1: parseFloat(participantData.judgeScores[0]) || 0,
        judgeScore2: parseFloat(participantData.judgeScores[1]) || 0,
        judgeScore3: parseFloat(participantData.judgeScores[2]) || 0,
        judgeScore4: parseFloat(participantData.judgeScores[3]) || 0,
        judgeScore5: parseFloat(participantData.judgeScores[4]) || 0,
        penalty: parseFloat(participantData.penalty) || 0,
        status: 'Vorläufig'
      };
      
      let result;
      if (resultId) {
        // Update bestehendes Ergebnis
        result = await resultsService.updateResult(resultId, resultData);
      } else {
        // Erstelle neues Ergebnis
        result = await resultsService.createResult(resultData);
        if (result.success && result.data?.id) {
          setSavedResults(prev => new Map(prev).set(participantId, result.data.id));
        }
      }
      
      if (!result || !result.success) {
        const errorMessage = result?.message || result?.error || 'Unbekannter Fehler beim Speichern';
        addToast('error', `Fehler beim Speichern: ${errorMessage}`);
        return;
      }
      
      addToast('success', 'Ergebnis erfolgreich gespeichert!');
      
      // Lade Ergebnisse neu, um Ränge zu aktualisieren
      await loadResults(groupId);
    } catch (error) {
      console.error('Fehler beim Speichern:', error);
      addToast('error', `Fehler beim Speichern: ${error.message || 'Unbekannter Fehler'}`);
    } finally {
      setSavingParticipants(prev => {
        const newSet = new Set(prev);
        newSet.delete(participantId);
        return newSet;
      });
    }
  };
  
  const loadResults = async (groupId) => {
    if (!groupId) return;
    
    try {
      const resultsResult = await resultsService.getResultsByGroup(groupId);
      if (resultsResult.success && resultsResult.data && Array.isArray(resultsResult.data)) {
        const existingResults = resultsResult.data;
        const savedResultsMap = new Map();
        
        // Erstelle Map mit gespeicherten Ergebnissen
        const savedResultsData = new Map();
        const statusesMap = new Map();
        
        existingResults.forEach(result => {
          const participantId = result.participantId || (result.nationId ? `nation-${result.nationId}` : null);
          if (participantId) {
            // Prüfe ob es ein tatsächliches Ergebnis ist (mit Scores) oder nur ein Platzhalter (DNS/Nachzügler ohne Results)
            const hasActualScores = result.judgeScore1 > 0 || result.judgeScore2 > 0 || result.judgeScore3 > 0 || 
                                     result.judgeScore4 > 0 || result.judgeScore5 > 0;
            
            if (hasActualScores) {
              const judgeScores = [
                parseFloat(result.judgeScore1) || 0,
                parseFloat(result.judgeScore2) || 0,
                parseFloat(result.judgeScore3) || 0,
                parseFloat(result.judgeScore4) || 0,
                parseFloat(result.judgeScore5) || 0
              ];
              const penalty = parseFloat(result.penalty) || 0;
              const averageScore = judgeScores.reduce((sum, s) => sum + s, 0) / judgeScores.length;
              const finalScore = averageScore + penalty;
              
              savedResultsData.set(participantId, {
                judgeScores,
                penalty,
                finalScore: finalScore.toFixed(2)
              });
              
              if (result.id) {
                savedResultsMap.set(participantId, result.id);
              }
            }
            
            // Speichere DNS- und Nachzügler-Status für alle Teilnehmer (auch ohne Results)
            statusesMap.set(participantId, {
              didNotStart: result.didNotStart || false,
              isLate: result.isLate || false,
              checkedByCaller: result.checkedByCaller || false
            });
          }
        });
        
        setParticipantStatuses(statusesMap);
        
        // Merge: Behalte nicht gespeicherte Änderungen, aktualisiere nur gespeicherte Ergebnisse
        setEditedData(prev => {
          const newMap = new Map(prev);
          
          // Aktualisiere gespeicherte Ergebnisse mit den neuesten Daten vom Server
          savedResultsData.forEach((data, participantId) => {
            // Nur aktualisieren, wenn das Ergebnis bereits gespeichert war (um nicht gespeicherte Änderungen nicht zu überschreiben)
            if (savedResultsMap.has(participantId)) {
              newMap.set(participantId, data);
            } else {
              // Wenn noch nicht gespeichert, aber Daten vom Server vorhanden sind, diese verwenden
              // (z.B. wenn jemand anders das Ergebnis gespeichert hat)
              if (!newMap.has(participantId)) {
                newMap.set(participantId, data);
              }
              // Wenn bereits lokale Änderungen vorhanden sind, diese behalten
            }
          });
          
          return newMap;
        });
        
        setSavedResults(savedResultsMap);
      }
    } catch (error) {
      console.warn('Fehler beim Laden der Ergebnisse:', error);
    }
  };

  const handleCompleteGroup = async () => {
    if (!currentEvent || !currentEvent.group) return;
    
    // Prüfe ob alle Teilnehmer (ohne DNS) Ergebnisse haben
    const participantsWithoutDNS = participants.filter(participant => {
      const status = participantStatuses.get(participant.id) || {};
      return !status.didNotStart; // Ignoriere DNS-Teilnehmer
    });
    
    const allParticipantsHaveResults = participantsWithoutDNS.every(participant => {
      return savedResults.has(participant.id);
    });
    
    if (!allParticipantsHaveResults) {
      addToast('error', 'Bitte für alle Teilnehmer (außer DNS) Ergebnisse speichern, bevor die Gruppe abgeschlossen wird');
      return;
    }
    
    try {
      setIsSaving(true);
      const groupId = currentEvent.group.id;
      
      // Verwende den complete Endpoint
      const headers = {
        'Content-Type': 'application/json'
      };
      
      if (typeof window !== 'undefined') {
        const token = localStorage.getItem('authToken');
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }
      }
      
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${baseURL}/results/group/${groupId}/complete`, {
        method: 'POST',
        headers
      });
      
      const responseData = await response.json();
      
      if (!response.ok || !responseData.success) {
        const errorMessage = responseData.message || responseData.error || 'Unbekannter Fehler beim Abschließen';
        addToast('error', `Fehler beim Abschließen: ${errorMessage}`);
        return;
      }
      
      addToast('success', 'Gruppe erfolgreich abgeschlossen! Das nächste Event wird gestartet.');
      
      // Lade Daten neu, um das neue Event zu sehen
      await loadData();
    } catch (error) {
      console.error('Fehler beim Abschließen:', error);
      addToast('error', `Fehler beim Abschließen: ${error.message || 'Unbekannter Fehler'}`);
    } finally {
      setIsSaving(false);
    }
  };


  // Wenn kein Venue zugewiesen ist, zeige Flächenauswahl
  if (!assignedVenueFromUser && !selectedVenue) {
    return (
      <TailwindPageWrapper>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-md mx-auto mt-16">
            <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm p-8">
              <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-4">
                {t("scorekeeper.selectVenue.title", "Fläche auswählen")}
              </h2>
              <p className="text-slate-600 dark:text-slate-400 mb-6">
                {t("scorekeeper.selectVenue.subtitle", "Bitte wählen Sie die Fläche aus, für die Sie Ergebnisse erfassen möchten.")}
              </p>
              <div className="space-y-3">
                <button
                  onClick={() => setSelectedVenue("Fläche A")}
                  className="w-full px-6 py-4 bg-white dark:bg-slate-700 border-2 border-slate-300 dark:border-slate-600 rounded-lg text-left hover:border-primary dark:hover:border-primary-light hover:bg-primary/5 dark:hover:bg-primary-light/10 transition-colors"
                >
                  <div className="font-semibold text-slate-900 dark:text-slate-100">Fläche A</div>
                  <div className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                    {t("scorekeeper.selectVenue.venueA", "Ergebnisse für Fläche A erfassen")}
                  </div>
                </button>
                <button
                  onClick={() => setSelectedVenue("Fläche B")}
                  className="w-full px-6 py-4 bg-white dark:bg-slate-700 border-2 border-slate-300 dark:border-slate-600 rounded-lg text-left hover:border-primary dark:hover:border-primary-light hover:bg-primary/5 dark:hover:bg-primary-light/10 transition-colors"
                >
                  <div className="font-semibold text-slate-900 dark:text-slate-100">Fläche B</div>
                  <div className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                    {t("scorekeeper.selectVenue.venueB", "Ergebnisse für Fläche B erfassen")}
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </TailwindPageWrapper>
    );
  }

  if (loading) {
    return (
      <TailwindPageWrapper>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-slate-600 dark:text-slate-400">Lade Daten...</p>
            </div>
          </div>
        </div>
      </TailwindPageWrapper>
    );
  }

  return (
    <TailwindPageWrapper>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
              {t("scorekeeper.title", "Punkteerfassung")}
            </h1>
            <p className="text-slate-600 dark:text-slate-400 mt-2">
              {t("scorekeeper.subtitle", "Fläche")}: <span className="font-semibold">{assignedVenue}</span>
            </p>
          </div>

          {/* Aktuelles Event */}
          {currentEvent ? (
            <div className="mb-8">
              <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm">
                <div className="bg-slate-50 dark:bg-slate-700/50 px-6 py-4 border-b border-slate-200 dark:border-slate-700">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
                        {t("scorekeeper.currentEvent", "Aktuelles Event")}
                      </h2>
                      <p className="text-slate-600 dark:text-slate-400 text-sm mt-1">
                        {currentEvent.group?.name || currentEvent.category} • {formatDate(currentEvent.startTime)} • {formatTime(currentEvent.startTime)} - {formatTime(currentEvent.endTime)}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => window.open(`/live-results?venue=${encodeURIComponent(assignedVenue)}`, '_blank')}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-primary dark:bg-primary-light text-white text-sm font-medium rounded-md hover:bg-primary-dark dark:hover:bg-primary transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20"
                        title="Live-Ergebnisse anzeigen"
                      >
                        <i className="fas fa-tv text-xs"></i>
                        Live-Ansicht
                      </button>
                      <div className="bg-green-100 dark:bg-green-900/30 px-3 py-1 rounded-full">
                        <span className="text-green-700 dark:text-green-300 text-sm font-medium">
                          {t("scorekeeper.status.active", "Aktiv")}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-6">
                  {/* Teilnehmerliste */}
                  {participants.length > 0 ? (() => {
                    // Sortiere alle Teilnehmer nach geplanter Reihenfolge (Startnummer), unabhängig von DNS-Status
                    const sortedParticipants = [...participants].sort((a, b) => {
                      const aGroup = a.groups?.find(g => g.id === currentEvent.group.id);
                      const bGroup = b.groups?.find(g => g.id === currentEvent.group.id);
                      const aStartNumber = aGroup ? aGroup.startNumber : 999;
                      const bStartNumber = bGroup ? bGroup.startNumber : 999;
                      
                      // Sortiere nur nach Startnummer (geplante Reihenfolge)
                      return aStartNumber - bStartNumber;
                    });
                    
                    // Trenne Nachzügler von der geplanten Reihenfolge
                    const plannedOrderParticipants = sortedParticipants.filter(p => {
                      const status = participantStatuses.get(p.id) || {};
                      return !status.isLate; // Alle Teilnehmer, die nicht nachträglich gekommen sind
                    });
                    
                    const lateParticipants = sortedParticipants.filter(p => {
                      const status = participantStatuses.get(p.id) || {};
                      return status.isLate; // Nur Nachzügler
                    });
                    
                    return (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
                        <thead className="bg-slate-50 dark:bg-slate-700/50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                              #
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                              {t("scorekeeper.athlete", "Athlet")}
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                              {t("scorekeeper.judgeScores", "Wertungsrichter-Punkte")}
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                              {t("scorekeeper.penalty", "Strafpunkte")}
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                              {t("scorekeeper.finalScore", "Endpunktzahl")}
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                              Aktion
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-slate-800 divide-y divide-slate-200 dark:divide-slate-700">
                            {/* Teilnehmer in geplanter Reihenfolge (inklusive DNS) */}
                            {plannedOrderParticipants.map((participant, index) => {
                            const participantData = editedData.get(participant.id) || {
                              judgeScores: [0, 0, 0, 0, 0],
                              penalty: 0,
                              finalScore: 0
                            };
                            const status = participantStatuses.get(participant.id) || {};
                            const didNotStart = status.didNotStart || false;
                            const nationCode = participant.nation?.code || participant.nationCode || 'DE';
                            
                            // Hole Startnummer für die Anzeige
                            const participantGroup = participant.groups?.find(g => g.id === currentEvent.group.id);
                            const startNumber = participantGroup ? participantGroup.startNumber : index + 1;
                            
                            return (
                                <tr 
                                  key={participant.id} 
                                  className={`${didNotStart ? 'opacity-60 grayscale' : 'hover:bg-slate-50/50 dark:hover:bg-slate-700/50'} transition-colors`}
                                >
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    {didNotStart ? (
                                      <span className="inline-flex items-center justify-center w-8 h-8 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-full font-semibold text-sm">
                                        DNS
                                      </span>
                                    ) : (
                                      <span className="inline-flex items-center justify-center w-8 h-8 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-full font-semibold text-sm">
                                        {startNumber}
                                      </span>
                                    )}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center gap-3">
                                      <img
                                        src={process.env.PUBLIC_URL + "/images/flags/" + nationCode + ".png"}
                                        alt={participant.nation?.name || participant.nation}
                                        className={`w-6 h-6 object-cover rounded-full border border-slate-200 dark:border-slate-600 ${didNotStart ? 'opacity-60' : ''}`}
                                      />
                                      <div>
                                        <div className={`text-sm font-medium flex items-center gap-2 ${didNotStart ? 'text-slate-500 dark:text-slate-400' : 'text-slate-900 dark:text-slate-100'}`}>
                                          {participant.name}
                                          {didNotStart && (
                                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300">
                                              <i className="fas fa-times-circle mr-1"></i>
                                              {t("scorekeeper.dns", "DNS")}
                                            </span>
                                          )}
                                        </div>
                                        <div className={`text-sm ${didNotStart ? 'text-slate-400 dark:text-slate-500' : 'text-slate-500 dark:text-slate-400'}`}>
                                          {participant.nation?.name || participant.nation}
                                        </div>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-6 py-4">
                                    <div className="grid grid-cols-5 gap-2">
                                      {participantData.judgeScores.map((score, judgeIndex) => (
                                        <div key={judgeIndex}>
                                          <label className={`block text-xs mb-1 ${didNotStart ? 'text-slate-400 dark:text-slate-500' : 'text-slate-500 dark:text-slate-400'}`}>
                                            R{judgeIndex + 1}
                                          </label>
                                          <input
                                            type="number"
                                            min="0"
                                            max="10"
                                            step="0.01"
                                            value={score}
                                            onChange={(e) => handleScoreChange(participant.id, judgeIndex, e.target.value)}
                                            disabled={didNotStart}
                                            className={`w-full px-2 py-1 border border-slate-300 dark:border-slate-600 rounded text-sm h-[32px] ${
                                              didNotStart 
                                                ? 'bg-slate-100 dark:bg-slate-700 text-slate-400 dark:text-slate-500 cursor-not-allowed' 
                                                : 'bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light'
                                            }`}
                                          />
                                        </div>
                                      ))}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <label className={`block text-xs mb-1 ${didNotStart ? 'text-slate-400 dark:text-slate-500' : 'text-slate-500 dark:text-slate-400'}`}>
                                      Strafpunkte
                                    </label>
                                    <input
                                      type="number"
                                      step="0.01"
                                      value={participantData.penalty || 0}
                                      onChange={(e) => handlePenaltyChange(participant.id, e.target.value)}
                                      disabled={didNotStart}
                                      className={`w-24 px-2 py-1 border border-slate-300 dark:border-slate-600 rounded text-sm h-[32px] ${
                                        didNotStart 
                                          ? 'bg-slate-100 dark:bg-slate-700 text-slate-400 dark:text-slate-500 cursor-not-allowed' 
                                          : 'bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light'
                                      }`}
                                    />
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className={`text-2xl font-bold ${didNotStart ? 'text-slate-400 dark:text-slate-500' : 'text-slate-900 dark:text-slate-100'}`}>
                                      {didNotStart ? '0.00' : (participantData.finalScore || "0.00")}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    {didNotStart ? (
                                      <span className="text-sm text-slate-400 dark:text-slate-500">-</span>
                                    ) : (
                                      <button
                                        onClick={() => handleSaveParticipantResult(participant.id)}
                                        disabled={savingParticipants.has(participant.id)}
                                        className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary dark:bg-primary-light text-white text-xs font-medium rounded-md hover:bg-primary-dark dark:hover:bg-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-primary/20"
                                        title={savedResults.has(participant.id) ? "Ergebnis aktualisieren" : "Ergebnis speichern"}
                                      >
                                        {savingParticipants.has(participant.id) ? (
                                          <>
                                            <i className="fas fa-spinner fa-spin text-xs"></i>
                                            Speichere...
                                          </>
                                        ) : savedResults.has(participant.id) ? (
                                          <>
                                            <i className="fas fa-sync-alt text-xs"></i>
                                            Aktualisieren
                                          </>
                                        ) : (
                                          <>
                                            <i className="fas fa-save text-xs"></i>
                                            Speichern
                                          </>
                                        )}
                                      </button>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                            
                            {/* Nachzügler-Sektion */}
                            {lateParticipants.length > 0 && (
                              <>
                                <tr>
                                  <td colSpan="6" className="px-6 py-3 bg-yellow-50 dark:bg-yellow-900/20 border-t-2 border-yellow-200 dark:border-yellow-800">
                                    <div className="flex items-center gap-2">
                                      <i className="fas fa-clock text-yellow-600 dark:text-yellow-400"></i>
                                      <span className="text-sm font-semibold text-yellow-800 dark:text-yellow-300">
                                        {t("scorekeeper.lateParticipants", "Nachzügler")}
                                      </span>
                                    </div>
                                  </td>
                                </tr>
                                {lateParticipants.map((participant, index) => {
                                  const participantData = editedData.get(participant.id) || {
                                    judgeScores: [0, 0, 0, 0, 0],
                                    penalty: 0,
                                    finalScore: 0
                                  };
                                  const nationCode = participant.nation?.code || participant.nationCode || 'DE';
                                  
                                  return (
                                    <tr key={participant.id} className="bg-yellow-50/30 dark:bg-yellow-900/10 hover:bg-yellow-50/50 dark:hover:bg-yellow-900/20 transition-colors">
                                      <td className="px-6 py-4 whitespace-nowrap">
                                        <span className="inline-flex items-center justify-center w-8 h-8 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 rounded-full font-semibold text-sm">
                                          {plannedOrderParticipants.length + index + 1}
                                        </span>
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center gap-3">
                                          <img
                                            src={process.env.PUBLIC_URL + "/images/flags/" + nationCode + ".png"}
                                            alt={participant.nation?.name || participant.nation}
                                            className="w-6 h-6 object-cover rounded-full border border-slate-200 dark:border-slate-600"
                                          />
                                          <div>
                                            <div className="text-sm font-medium text-slate-900 dark:text-slate-100 flex items-center gap-2">
                                              {participant.name}
                                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300">
                                                <i className="fas fa-clock mr-1"></i>
                                                {t("scorekeeper.late", "Verspätet")}
                                              </span>
                                            </div>
                                            <div className="text-sm text-slate-500 dark:text-slate-400">
                                              {participant.nation?.name || participant.nation}
                                            </div>
                                          </div>
                                        </div>
                                      </td>
                                      <td className="px-6 py-4">
                                        <div className="grid grid-cols-5 gap-2">
                                          {participantData.judgeScores.map((score, judgeIndex) => (
                                            <div key={judgeIndex}>
                                              <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1">
                                                R{judgeIndex + 1}
                                              </label>
                                              <input
                                                type="number"
                                                min="0"
                                                max="10"
                                                step="0.01"
                                                value={score}
                                                onChange={(e) => handleScoreChange(participant.id, judgeIndex, e.target.value)}
                                                className="w-full px-2 py-1 border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light text-sm h-[32px]"
                                              />
                                            </div>
                                          ))}
                                        </div>
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="space-y-2">
                                          <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1">
                                            Strafpunkte
                                          </label>
                                          <input
                                            type="number"
                                            step="0.01"
                                            value={participantData.penalty || 0}
                                            onChange={(e) => handlePenaltyChange(participant.id, e.target.value)}
                                            className="w-24 px-2 py-1 border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light text-sm h-[32px]"
                                          />
                                        </div>
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                                          {participantData.finalScore || "0.00"}
                                        </div>
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap">
                                        <button
                                          onClick={() => handleSaveParticipantResult(participant.id)}
                                          disabled={savingParticipants.has(participant.id)}
                                          className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary dark:bg-primary-light text-white text-xs font-medium rounded-md hover:bg-primary-dark dark:hover:bg-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-primary/20"
                                          title={savedResults.has(participant.id) ? "Ergebnis aktualisieren" : "Ergebnis speichern"}
                                        >
                                          {savingParticipants.has(participant.id) ? (
                                            <>
                                              <i className="fas fa-spinner fa-spin text-xs"></i>
                                              Speichere...
                                            </>
                                          ) : savedResults.has(participant.id) ? (
                                            <>
                                              <i className="fas fa-sync-alt text-xs"></i>
                                              Aktualisieren
                                            </>
                                          ) : (
                                            <>
                                              <i className="fas fa-save text-xs"></i>
                                              Speichern
                                            </>
                                          )}
                                        </button>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </>
                            )}
                        </tbody>
                      </table>
                    </div>
                    );
                  })() : (
                    <div className="text-center py-12">
                      <p className="text-slate-500 dark:text-slate-400">
                        {t("scorekeeper.noParticipants", "Keine Teilnehmer für dieses Event gefunden")}
                      </p>
                    </div>
                  )}

                  {/* Gruppe abschließen Button */}
                  {participants.length > 0 && (
                    <div className="mt-6 flex justify-end">
                      <button
                        onClick={handleCompleteGroup}
                        disabled={isSaving}
                        className="inline-flex items-center gap-2 px-6 py-3 bg-green-600 dark:bg-green-700 text-white font-medium rounded-md hover:bg-green-700 dark:hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-green-500/20"
                      >
                        {isSaving ? (
                          <>
                            <i className="fas fa-spinner fa-spin mr-2"></i>
                            Gruppe wird abgeschlossen...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-check-circle mr-2"></i>
                            Gruppe abschließen
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="mb-8 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm p-12">
              <div className="text-center">
                <div className="bg-slate-100 dark:bg-slate-700 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <i className="fas fa-clock text-slate-400 dark:text-slate-500 text-2xl"></i>
                </div>
                <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">
                  {t("scorekeeper.noActiveEvent", "Kein aktives Event")}
                </h3>
                <p className="text-slate-500 dark:text-slate-400">
                  {t("scorekeeper.noActiveEventDesc", "Es läuft derzeit kein Event auf dieser Fläche")}
                </p>
              </div>
            </div>
          )}

          {/* Nächste Events */}
          {upcomingEventsWithCounts.length > 0 && (
            <div>
              <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-4">
                {t("scorekeeper.upcomingEvents", "Kommende Events")}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {upcomingEventsWithCounts.map((event) => (
                  <div
                    key={event.id}
                    className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm p-4"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-medium text-slate-900 dark:text-slate-100">
                        {event.group?.name || event.category}
                      </h3>
                      <span className={`text-xs px-2 py-1 rounded-full inline-flex items-center ${
                        event.status === 'Vorbereitung' 
                          ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300'
                          : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300'
                      }`}>
                        <i className={`fas ${
                          event.status === 'Vorbereitung' ? 'fa-hourglass-half' : 'fa-clock'
                        } mr-1.5`}></i>
                        {event.status === 'Vorbereitung' 
                          ? t("scorekeeper.status.preparation", "Vorbereitung")
                          : t("scorekeeper.status.pending", "Ausstehend")
                        }
                      </span>
                    </div>
                    <div className="text-sm text-slate-500 dark:text-slate-400 mb-2">
                      {formatDate(event.startTime)} • {formatTime(event.startTime)} - {formatTime(event.endTime)}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                      <i className="fas fa-users text-xs"></i>
                      <span>{event.participantCount || 0} {event.participantCount === 1 ? 'Teilnehmer' : 'Teilnehmer'}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Toast */}
          {toast.show && (
            <div className={`fixed bottom-4 right-4 px-6 py-4 rounded-lg shadow-lg z-50 ${
              toast.type === 'success' 
                ? 'bg-green-500 text-white' 
                : toast.type === 'error'
                ? 'bg-red-500 text-white'
                : 'bg-blue-500 text-white'
            }`}>
              <div className="flex items-center gap-3">
                <i className={`fas ${
                  toast.type === 'success' ? 'fa-check-circle' :
                  toast.type === 'error' ? 'fa-exclamation-circle' :
                  'fa-info-circle'
                }`}></i>
                <span>{toast.message}</span>
              </div>
            </div>
          )}
        </div>
      </TailwindPageWrapper>
  );
};

export default ScorekeeperView;

